import {
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { queryClient } from '@/routes/__root'
import { api, useAuth } from '@/lib/api'
import type { UpscaleResult, UpscaleSubmitPayload } from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import { GripVertical, Image as ImageIcon, Loader2, Upload, X } from 'lucide-react'

type UpscaleSearch = {
  generationId?: string
  assetIndex?: number
  modelSlug?: string
  prompt?: string
  steps?: number
  cfgScale?: number
  clipSkip?: number
  seedMode?: 'fixed' | 'random'
  seed?: number
  loras?: string
  upscaleFactor?: number
  tileScale?: number
  workflowVariant?: string
}

type SourceSelection =
  | {
      type: 'generation'
      generationId: string
      assetIndex: number
      preview?: string | null
      width?: number | null
      height?: number | null
    }
  | {
      type: 'upload'
      uploadId: string
      preview: string
      width: number
      height: number
      mimeType: string
    }
  | null

export const Route = createFileRoute('/upscale/')({
  validateSearch: (search: Record<string, unknown>): UpscaleSearch => {
    const parseNumber = (value: unknown): number | undefined => {
      if (typeof value === 'number' && !Number.isNaN(value)) return value
      if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value)
        if (!Number.isNaN(parsed)) return parsed
      }
      return undefined
    }

    const seedMode =
      typeof search.seedMode === 'string' && (search.seedMode === 'fixed' || search.seedMode === 'random')
        ? (search.seedMode as 'fixed' | 'random')
        : undefined

    return {
      generationId: typeof search.generationId === 'string' ? search.generationId : undefined,
      assetIndex: parseNumber(search.assetIndex),
      modelSlug: typeof search.modelSlug === 'string' ? search.modelSlug : undefined,
      prompt: typeof search.prompt === 'string' ? search.prompt : undefined,
      steps: parseNumber(search.steps),
      cfgScale: parseNumber(search.cfgScale),
      clipSkip: parseNumber(search.clipSkip),
      seedMode,
      seed: parseNumber(search.seed),
      loras: typeof search.loras === 'string' ? search.loras : undefined,
      upscaleFactor: parseNumber(search.upscaleFactor),
      tileScale: parseNumber(search.tileScale),
      workflowVariant: typeof search.workflowVariant === 'string' ? search.workflowVariant : undefined,
    }
  },
  beforeLoad: async () => {
    const user = queryClient.getQueryData(['user'])
    if (!user) {
      try {
        await queryClient.ensureQueryData({ queryKey: ['user'], queryFn: api.auth.getCurrentUser })
      } catch (error) {
        throw redirect({ to: '/auth/login', search: { redirect: '/upscale' } })
      }
    }
  },
  loader: () =>
    queryClient.ensureQueryData({ queryKey: ['upscale', 'onload'], queryFn: api.upscale.onLoad }),
  component: UpscalePage,
})

function UpscalePage() {
  const search = Route.useSearch()

  const { isAuthenticated } = useAuth()

  const queueQuery = useQuery({
    queryKey: ['queue', 'updates'],
    queryFn: api.queue.updates,
    enabled: isAuthenticated,
    refetchInterval: (query) => {
      const length = query.state.data?.queue.length ?? 0
      return length > 0 ? 3000 : false
    },
    refetchOnWindowFocus: isAuthenticated,
  })

  const recentCompletionIdsRef = useRef<string[]>([])
  const pendingDetailFetch = useRef<Set<string>>(new Set())
  const [highlightedResultId, setHighlightedResultId] = useState<string | null>(null)

  const { data: onloadData, isLoading, refetch } = useQuery({
    queryKey: ['upscale', 'onload'],
    queryFn: api.upscale.onLoad,
    staleTime: 10_000,
  })

  useEffect(() => {
    if (!queueQuery.data) return

    const recentIds = queueQuery.data.recent.map((item) => item.generation_id)

    if (recentCompletionIdsRef.current.length === 0) {
      recentCompletionIdsRef.current = recentIds
      return
    }

    const newIds = recentIds.filter((id) => !recentCompletionIdsRef.current.includes(id))
    if (newIds.length === 0) {
      recentCompletionIdsRef.current = recentIds
      return
    }

    newIds.forEach((generationId) => {
      if (pendingDetailFetch.current.has(generationId)) return
      pendingDetailFetch.current.add(generationId)

      void api.generation
        .detail(generationId, false)
        .then(async (detail) => {
          if (detail.job_type !== 'upscale') return
          const result = await refetch()
          const hasResult = result.data?.results?.some((item) => item.generation_id === generationId)
          if (hasResult) {
            setHighlightedResultId(generationId)
          }
        })
        .catch(() => {})
        .finally(() => {
          pendingDetailFetch.current.delete(generationId)
        })
    })

    recentCompletionIdsRef.current = recentIds
  }, [queueQuery.data, refetch])

  useEffect(() => {
    if (!highlightedResultId) return
    const timeout = window.setTimeout(() => setHighlightedResultId(null), 6000)
    return () => window.clearTimeout(timeout)
  }, [highlightedResultId])

  const prefillQuery = useQuery({
    queryKey: ['upscale', 'prefill', search.generationId],
    queryFn: () => api.generation.detail(search.generationId!, true),
    enabled: !!search.generationId,
  })

  const [source, setSource] = useState<SourceSelection>(null)
  const [modelSlug, setModelSlug] = useState<string | undefined>(search.modelSlug)
  const [prompt, setPrompt] = useState<string | undefined>(search.prompt)
  const [steps, setSteps] = useState<number | undefined>(search.steps)
  const [cfgScale, setCfgScale] = useState<number | undefined>(search.cfgScale)
  const [clipSkip, setClipSkip] = useState<number | undefined>(search.clipSkip)
  const [seedMode, setSeedMode] = useState<'fixed' | 'random'>(search.seedMode ?? 'random')
  const [seed, setSeed] = useState<number | undefined>(search.seed)
  // Always use basic_upscale for upscale operations, ignore URL params
  const workflowVariant = 'basic_upscale'
  const [loras, setLoras] = useState<Array<{ id: number; strength: number }>>(() => parseLoraString(search.loras))
  const [upscaleFactor, setUpscaleFactor] = useState<2 | 3 | 4>(() => {
    const factor = search.upscaleFactor
    return factor === 3 || factor === 4 ? factor : 2
  })
  const [tileScale, setTileScale] = useState<0.5 | 1>(() => (search.tileScale === 1 ? 1 : 0.5))

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const prefillApplied = useRef(false)

  useEffect(() => {
    if (!search.generationId) {
      prefillApplied.current = true
      return
    }
    prefillApplied.current = false
  }, [search.generationId])

  useEffect(() => {
    if (!prefillQuery.data || !search.generationId || prefillApplied.current) return

    const detail = prefillQuery.data
    const assetIndex = search.assetIndex ?? 0
    const asset = detail.assets.find((item) => (item.index ?? 0) === assetIndex) ?? detail.assets[0]

      // Try multiple sources for the image URL
    let imageUrl = asset?.asset_url ?? asset?.download_url ?? null

    // If we have a download_url but it's relative, use it as-is
    // Media files are proxied through Vite dev server to backend
    if (asset?.download_url && !asset.download_url.startsWith('http') && !asset.download_url.startsWith('data:')) {
      imageUrl = asset.download_url
    }

    // Fallback: construct download URL if none exists
    if (!imageUrl && asset?.index !== undefined) {
      imageUrl = `/api/generation/${detail.generation_id}/download/${asset.index}`
    }

    setSource({
      type: 'generation',
      generationId: detail.generation_id,
      assetIndex: asset?.index ?? 0,
      preview: imageUrl,
      width: asset?.width ?? detail.width,
      height: asset?.height ?? detail.height,
    })

    setModelSlug((prev) => prev ?? detail.base_model)
    setPrompt((prev) => prev ?? detail.prompt)
    setSteps((prev) => (prev ?? detail.steps))
    setCfgScale((prev) => (prev ?? detail.cfg_scale))
    setClipSkip((prev) => (prev ?? detail.clip_skip))
    setSeedMode(search.seedMode ?? (detail.seed_mode === 'fixed' ? 'fixed' : 'random'))
    setSeed((prev) => prev ?? detail.seed ?? undefined)
    // workflowVariant is always 'basic_upscale' for upscale operations, don't inherit from source
    if (!loras.length && detail.loras.length > 0) {
      setLoras(detail.loras.map((lora) => ({ id: lora.id, strength: lora.strength })))
    }

    prefillApplied.current = true
  }, [prefillQuery.data, search.generationId, search.seedMode, loras.length])

  useEffect(() => {
    if (!onloadData?.models || modelSlug) return
    setModelSlug(onloadData.models[0]?.slug)
  }, [onloadData?.models, modelSlug])

  useEffect(() => {
    if (prefillQuery.isError) {
      toast.error('Failed to load source generation details')
    }
  }, [prefillQuery.isError])

  const uploadMutation = useMutation({
    mutationFn: api.upscale.upload,
    onSuccess: (data) => {
      setSource({
        type: 'upload',
        uploadId: data.upload_id,
        preview: data.preview_url,
        width: data.width,
        height: data.height,
        mimeType: data.mime_type,
      })
      setSeedMode('random')
      setSeed(undefined)
      toast.success('Image uploaded')
    },
    onError: (error: any) => {
      toast.error(error?.message ?? 'Failed to upload image')
    },
  })

  const submitMutation = useMutation({
    mutationFn: (payload: UpscaleSubmitPayload) => api.upscale.submit(payload),
    onSuccess: async () => {
      toast.success('Upscale queued')
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['queue', 'updates'] }),
        queryClient.invalidateQueries({ queryKey: ['generation', 'onload'] }),
        refetch(),
      ])
    },
    onError: (error: any) => {
      toast.error(error?.message ?? 'Failed to queue upscale')
    },
  })

  const handleFileSelection = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    uploadMutation.mutate(file)
    event.target.value = ''
  }

  const canSubmit = Boolean(source && modelSlug) && !submitMutation.isPending && !uploadMutation.isPending

  const handleSubmit = () => {
    if (!source || !modelSlug) {
      toast.error('Select a model and source image first')
      return
    }

    const payload: UpscaleSubmitPayload = {
      model_slug: modelSlug,
      upscale_factor: upscaleFactor,
      tile_scale: tileScale,
      workflow_variant: workflowVariant,
      prompt,
      steps,
      cfg_scale: cfgScale,
      clip_skip: clipSkip,
      seed_mode: seedMode,
      seed: seedMode === 'fixed' ? seed : undefined,
    }

    if (source.type === 'generation') {
      payload.source_generation_id = source.generationId
      payload.asset_index = source.assetIndex
      // LoRAs are handled server-side for generation sources
    } else {
      payload.upload_id = source.uploadId
      if (loras.length > 0) {
        payload.loras = loras
      }
    }

    submitMutation.mutate(payload)
  }

  if (isLoading || !onloadData) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const results = onloadData.results ?? []

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header>
        <h1 className="text-2xl font-semibold">Upscale image</h1>
        <p className="text-sm text-muted-foreground">
          Queue an upscale job and immediately compare the result against the original with an interactive slider.
        </p>
      </header>

      <div className="space-y-6">
        <section className="space-y-6">
          <Card>
            <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle className="text-base">Source image</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Upload a new image or pass parameters from the gallery popup.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadMutation.isPending}
                >
                  {uploadMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload image
                    </>
                  )}
                </Button>
                {source && (
                  <Button variant="ghost" size="sm" onClick={() => setSource(null)}>
                    <X className="mr-1 h-4 w-4" />
                    Clear
                  </Button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelection}
              />
            </CardHeader>
            <CardContent>
              {source ? (
                <div className="grid items-start gap-4 md:grid-cols-[220px,1fr]">
                  <div className="flex h-full max-w-[220px] w-full items-center justify-center overflow-hidden rounded-md border border-border bg-muted/20">
                    {source.preview ? (
                      <img src={source.preview} alt="Source" className="h-full w-full object-cover" />
                    ) : (
                      <ImageIcon className="h-10 w-10 text-muted-foreground" />
                    )}
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="font-medium">
                      {source.type === 'generation' ? 'From generation history' : 'Uploaded image'}
                    </p>
                    <p className="text-muted-foreground">
                      Dimensions: {formatDimensions(source.width, source.height)}
                    </p>
                    {source.type === 'generation' && (
                      <p className="text-muted-foreground">Generation ID: {source.generationId}</p>
                    )}
                    {source.type === 'upload' && (
                      <p className="text-muted-foreground">Format: {source.mimeType}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border py-12 text-center text-sm text-muted-foreground">
                  <ImageIcon className="h-10 w-10" />
                  <p>No source selected yet. Upload an image or open this page from the gallery's upscale button.</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Upscale settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select value={modelSlug} onValueChange={setModelSlug}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {onloadData.models.map((model) => (
                        <SelectItem key={model.slug} value={model.slug}>
                          {model.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Upscale factor</Label>
                  <Select
                    value={String(upscaleFactor)}
                    onValueChange={(value) => setUpscaleFactor(Number(value) as 2 | 3 | 4)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2×</SelectItem>
                      <SelectItem value="3">3×</SelectItem>
                      <SelectItem value="4">4×</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Tile size</Label>
                  <Select value={String(tileScale)} onValueChange={(value) => setTileScale(Number(value) as 0.5 | 1)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.5">Half tiles (0.5×)</SelectItem>
                      <SelectItem value="1">Full tiles (1×)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button className="min-w-[160px]" disabled={!canSubmit} onClick={handleSubmit}>
              {submitMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Queuing...
                </>
              ) : (
                'Queue upscale'
              )}
            </Button>
          </div>
        </section>

        <section className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-4">Recent upscales</h2>
            <UpscaleResultsSection results={results} highlightedId={highlightedResultId} />
          </div>
        </section>
      </div>
    </div>
  )
}

function parseLoraString(value?: string): Array<{ id: number; strength: number }> {
  if (!value) return []
  return value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [idPart, strengthPart] = entry.split(':')
      const id = Number(idPart)
      const strength = strengthPart !== undefined ? Number(strengthPart) : 1
      if (Number.isNaN(id) || Number.isNaN(strength)) return null
      return { id, strength }
    })
    .filter((item): item is { id: number; strength: number } => item !== null)
}

function UpscaleResultsSection({
  results,
  highlightedId,
}: {
  results: UpscaleResult[]
  highlightedId: string | null
}) {
  if (results.length === 0) {
    return (
      <Card className="h-full min-h-[360px] border-dashed">
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
          <ImageIcon className="h-10 w-10 text-muted-foreground" />
          <div className="space-y-1">
            <h2 className="text-base font-medium">No upscales yet</h2>
            <p className="text-sm text-muted-foreground">
              Queue an upscale job to see the original and enhanced image side-by-side.
            </p>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {results.map((result) => (
        <UpscaleResultCard
          key={result.generation_id}
          result={result}
          isHighlighted={highlightedId === result.generation_id}
        />
      ))}
    </div>
  )
}

function UpscaleResultCard({ result, isHighlighted }: { result: UpscaleResult; isHighlighted: boolean }) {
  const createdAtLabel = useMemo(() => new Date(result.created_at).toLocaleString(), [result.created_at])
  const aspectRatio = useMemo(() => {
    if (result.upscaled.width && result.upscaled.height) {
      return result.upscaled.height / result.upscaled.width
    }
    return undefined
  }, [result.upscaled.height, result.upscaled.width])

  const scaleLabel = useMemo(() => {
    if (result.source.width && result.upscaled.width) {
      const ratio = result.upscaled.width / result.source.width
      if (Number.isFinite(ratio)) {
        return `${ratio.toFixed(2)}×`
      }
    }
    return '—'
  }, [result.source.width, result.upscaled.width])

  const sourceLabel = result.source_type === 'generation' ? 'From generation history' : 'Uploaded image'

  return (
    <article
      className={cn(
        'rounded-lg border border-border/80 bg-card/70 shadow-sm transition-shadow',
        isHighlighted && 'border-primary shadow-primary/30 ring-2 ring-primary/40',
      )}
    >
      <div className="border-b border-border/60 bg-muted/30 px-4 py-3">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-foreground">{createdAtLabel}</p>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            {result.base_model ? `Model · ${result.base_model}` : 'Upscale job'}
          </p>
        </div>
        {result.prompt && (
          <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{result.prompt}</p>
        )}
      </div>
      <div className="space-y-4 p-4">
        <BeforeAfterSlider beforeSrc={result.source.url} afterSrc={result.upscaled.url} aspectRatio={aspectRatio} />

        <div className="grid gap-3 text-xs text-muted-foreground sm:grid-cols-3">
          <div className="space-y-1">
            <p className="font-medium text-foreground">Original</p>
            <p>{sourceLabel}</p>
            <p>Size: {formatDimensions(result.source.width, result.source.height)}</p>
          </div>
          <div className="space-y-1">
            <p className="font-medium text-foreground">Upscaled</p>
            <p>Size: {formatDimensions(result.upscaled.width, result.upscaled.height)}</p>
            <p>Scale: {scaleLabel}</p>
          </div>
          <div className="space-y-1 break-all">
            <p className="font-medium text-foreground">Generation ID</p>
            <p>{result.generation_id}</p>
          </div>
        </div>
      </div>
    </article>
  )
}

function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  aspectRatio,
}: {
  beforeSrc: string
  afterSrc: string
  aspectRatio?: number
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState(50)
  const draggingRef = useRef(false)

  const ratioPercent = useMemo(() => {
    if (!aspectRatio || aspectRatio <= 0 || Number.isNaN(aspectRatio)) {
      return 75
    }
    const percent = aspectRatio * 100
    return Math.min(140, Math.max(45, percent))
  }, [aspectRatio])

  const updatePosition = useCallback((clientX: number) => {
    const container = containerRef.current
    if (!container) return
    const rect = container.getBoundingClientRect()
    if (rect.width === 0) return
    const ratio = ((clientX - rect.left) / rect.width) * 100
    const clamped = Math.min(100, Math.max(0, ratio))
    setPosition(clamped)
  }, [])

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      if (!draggingRef.current) return
      updatePosition(event.clientX)
    }

    const handlePointerUp = () => {
      draggingRef.current = false
    }

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [updatePosition])

  const handleContainerPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      draggingRef.current = true
      updatePosition(event.clientX)
    },
    [updatePosition],
  )

  const handleHandlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.stopPropagation()
      draggingRef.current = true
      updatePosition(event.clientX)
    },
    [updatePosition],
  )

  const handleKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault()
      setPosition((prev) => Math.max(0, prev - 5))
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault()
      setPosition((prev) => Math.min(100, prev + 5))
    } else if (event.key === 'Home') {
      event.preventDefault()
      setPosition(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      setPosition(100)
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-lg border border-border/70 bg-background/40"
      style={{ paddingTop: `${ratioPercent}%` }}
      onPointerDown={handleContainerPointerDown}
    >
      {/* Both images as backgrounds, same size */}
      <img src={afterSrc} alt="Upscaled image" className="absolute inset-0 h-full w-full object-contain" />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}>
        <img src={beforeSrc} alt="Original image" className="absolute inset-0 h-full w-full object-contain" />
      </div>

      {/* Slider line */}
      <div className="pointer-events-none absolute inset-y-0" style={{ left: `${position}%` }}>
        <div className="h-full w-[2px] -translate-x-1/2 bg-white/70 shadow-[0_0_12px_rgba(0,0,0,0.35)]" />
      </div>

      {/* Instructions */}
      <div className="absolute left-1/2 top-4 -translate-x-1/2">
        <span className="rounded-full bg-black/60 px-2 py-1 text-xs font-medium uppercase tracking-wide text-white">
          Slide to compare
        </span>
      </div>

      {/* Slider handle */}
      <div
        className="absolute top-1/2 -translate-y-1/2"
        style={{ left: `${position}%` }}
      >
        <div
          role="slider"
          tabIndex={0}
          aria-label="Reveal upscaled image"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(position)}
          className="flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full border border-white/60 bg-black/70 text-white shadow-lg outline-none transition-colors focus-visible:ring focus-visible:ring-primary/50"
          onPointerDown={handleHandlePointerDown}
          onKeyDown={handleKeyDown}
        >
          <GripVertical className="h-4 w-4" />
        </div>
      </div>

      {/* Labels - correctly positioned to match the slider behavior */}
      <span className="absolute left-4 top-4 rounded-full bg-black/60 px-2 py-1 text-xs font-medium uppercase tracking-wide text-white">
        Original
      </span>
      <span className="absolute right-4 top-4 rounded-full bg-black/60 px-2 py-1 text-xs font-medium uppercase tracking-wide text-white">
        Upscaled
      </span>
    </div>
  )
}

function formatDimensions(width?: number | null, height?: number | null) {
  if (!width || !height) return '—'
  return `${width.toLocaleString()} × ${height.toLocaleString()}`
}
